/**
 * The routines the Routines page lists once packs are on, the same on the web
 * and on the phone. Pure logic over the packs list and the routines list.
 */

import type { PackResp } from "@/types/packs.ts";

/** The fields this module reads off a routine. */
export type PackRoutineItem = { kind: string; systemRoutineId?: string; enabled: boolean };

/**
 * `routines` without the system routines of packs the user has not installed.
 * Kept: the user's own routines, a routine in no pack, a routine of an
 * installed pack, and any routine that is on — so removing a pack, or a
 * routine joining a pack, never hides a routine the user turned on.
 *
 * `keep` names system routines to keep whatever their state: the page passes
 * the ones it has shown, so a routine the user turns off stays where they can
 * turn it on again until the page next opens.
 */
export const routinesWithPacks = <T extends PackRoutineItem>(
  routines: readonly T[],
  packs: readonly PackResp[],
  keep: ReadonlySet<string> = new Set(),
): T[] => {
  const inPack = new Set<string>();
  const inInstalledPack = new Set<string>();
  for (const pack of packs) {
    for (const id of pack.routines ?? []) {
      inPack.add(id);
      if (pack.installed) inInstalledPack.add(id);
    }
  }
  return routines.filter(
    (r) =>
      r.kind !== "system" ||
      r.enabled ||
      !r.systemRoutineId ||
      !inPack.has(r.systemRoutineId) ||
      inInstalledPack.has(r.systemRoutineId) ||
      keep.has(r.systemRoutineId),
  );
};
